import React, { useState, useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { Plus, Search, Edit, Trash2, ChevronLeft, ChevronRight } from 'lucide-react'
import toast from 'react-hot-toast'
import { showDeleteConfirm, showSuccess, showError } from '@/lib/swal'

import { PageHeader } from '@/components/shared/PageHeader'
import { Card, CardContent, CardHeader } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useAuthStore } from '@/store/auth.store'
import { empDocumentService } from '@/services/empDocumentService'
import type { EmpDocumentListItem } from '@/api/types'

export const EmpDocumentsList: React.FC = () => {
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const branchId = user?.selectedBranch?.branchId

  const [documents, setDocuments] = useState<EmpDocumentListItem[]>([])
  const [loading, setLoading] = useState(false)
  const [pageNumber, setPageNumber] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [totalRecords, setTotalRecords] = useState(0)
  const [searchQuery, setSearchQuery] = useState('')
  const [deletingId, setDeletingId] = useState<number | null>(null)

  useEffect(() => {
    if (!branchId) return
    const fetchDocuments = async () => {
      try {
        setLoading(true)
        const res = await empDocumentService.getList(branchId, pageNumber, pageSize)
        setDocuments(res.data ?? [])
        setTotalRecords(res.totalRecords ?? 0)
      } catch (error: any) {
        const status = error?.response?.status
        const msg = (error?.response?.data?.message || error?.message || '').toLowerCase()
        const isNoData = status === 404 || /not found|no data|no records/.test(msg)
        setDocuments([])
        setTotalRecords(0)
        if (!isNoData) {
          toast.error(error?.response?.data?.message ?? error?.message ?? 'Failed to fetch documents')
        }
      } finally {
        setLoading(false)
      }
    }
    fetchDocuments()
  }, [branchId, pageNumber, pageSize])

  const filteredDocuments = useMemo(() => {
    if (!searchQuery.trim()) return documents
    const q = searchQuery.toLowerCase()
    return documents.filter(
      (d) =>
        (d.documentName ?? '').toLowerCase().includes(q) ||
        (d.documentDiscription ?? '').toLowerCase().includes(q)
    )
  }, [documents, searchQuery])

  const totalPages = Math.max(1, Math.ceil(totalRecords / pageSize))

  const formatDate = (dateString?: string) => {
    if (!dateString) return '—'
    try {
      const d = new Date(dateString)
      return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
    } catch {
      return String(dateString).slice(0, 10)
    }
  }

  const handleAdd = () => navigate('/emp-documents/new')
  const handleEdit = (id: number) => navigate(`/emp-documents/${id}/edit`)

  const handleDelete = async (id: number) => {
    const confirmed = await showDeleteConfirm('Delete Document', 'Are you sure you want to delete this document?')
    if (!confirmed) return
    setDeletingId(id)
    try {
      await empDocumentService.deleteDocument(id)
      await showSuccess('Deleted', 'Document deleted successfully.')
      if (branchId) {
        const res = await empDocumentService.getList(branchId, pageNumber, pageSize)
        setDocuments(res.data ?? [])
        setTotalRecords(res.totalRecords ?? 0)
      }
    } catch (e: any) {
      await showError('Delete failed', e?.response?.data?.message ?? e?.message ?? 'Failed to delete.')
    } finally {
      setDeletingId(null)
    }
  }

  if (!branchId) {
    return (
      <div className="space-y-6 p-6">
        <PageHeader title="Employee Documents" description="Manage employee documents." />
        <Card>
          <CardContent className="p-6 text-center text-muted-foreground">
            Please select a branch first.
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-6 p-6">
      <PageHeader
        title="Employee Documents"
        description="View and manage employee documents."
        action={{ label: 'Add', onClick: handleAdd, icon: Plus }}
      />
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <div className="relative w-64">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
            />
          </div>
        </CardHeader>
        <CardContent>
          <div className="rounded-md border overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="px-3 text-left">Name</TableHead>
                    <TableHead className="px-3 text-left">Description</TableHead>
                    <TableHead className="px-3 text-left">Created Date</TableHead>
                    <TableHead className="px-3 text-left">Document</TableHead>
                    <TableHead className="px-3 w-[100px] text-right">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                      Loading...
                    </TableCell>
                  </TableRow>
                ) : filteredDocuments.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                      No data available in table
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredDocuments.map((doc) => (
                    <TableRow key={doc.id}>
                      <TableCell className="px-3 font-medium text-left">
                        {doc.documentName ?? '—'}
                      </TableCell>
                      <TableCell className="px-3 max-w-[200px] truncate text-left">
                        {doc.documentDiscription ?? '—'}
                      </TableCell>
                      <TableCell className="px-3 text-left">
                        {formatDate(doc.createdDate)}
                      </TableCell>
                      <TableCell className="px-3 text-left">
                        {doc.documentAttachUrl ? (
                          <a
                            href={doc.documentAttachUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-primary hover:underline"
                          >
                            View
                          </a>
                        ) : (
                          '—'
                        )}
                      </TableCell>
                      <TableCell className="px-3 text-right">
                        <div className="flex justify-end gap-1">
                          <Button variant="ghost" size="sm" onClick={() => handleEdit(doc.id!)} className="h-8 w-8 p-0">
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDelete(doc.id!)}
                            disabled={deletingId === doc.id}
                            className="h-8 w-8 p-0 text-destructive hover:text-destructive"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
          <div className="flex flex-wrap items-center justify-between gap-4 mt-4">
            <div className="text-sm text-muted-foreground">
              Showing {(pageNumber - 1) * pageSize + 1} to{' '}
              {Math.min(pageNumber * pageSize, totalRecords)} of {totalRecords} entries
            </div>
            <div className="flex items-center gap-2">
              <Select
                value={String(pageSize)}
                onValueChange={(v) => {
                  setPageSize(Number(v))
                  setPageNumber(1)
                }}
              >
                <SelectTrigger className="w-[90px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[10, 25, 50, 100].map((n) => (
                    <SelectItem key={n} value={String(n)}>
                      {n}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <span className="text-sm text-muted-foreground">per page</span>
              <Button
                variant="outline"
                size="sm"
                disabled={pageNumber <= 1 || loading}
                onClick={() => setPageNumber((p) => Math.max(1, p - 1))}
              >
                <ChevronLeft className="h-4 w-4" />
                Previous
              </Button>
              <span className="text-sm text-muted-foreground px-2">
                Page {pageNumber} of {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                disabled={pageNumber >= totalPages || loading || totalRecords === 0}
                onClick={() => setPageNumber((p) => p + 1)}
              >
                Next
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
